import TagManager from 'react-gtm-module';
import SHA256 from 'crypto-js/sha256';

/**
 * Generate a semi-unique event_id for GA4 / Meta CAPI deduplication.
 * Format: {eventName}_{timestamp}_{random5digits}
 * @param {string} eventName - Name of the event (used as prefix)
 * @returns {string} Unique event identifier string
 */
const generateEventId = (eventName = 'evt') => {
  const ts = Date.now();
  const rand = Math.floor(Math.random() * 1e5)
    .toString()
    .padStart(5, '0');
  return `${eventName}_${ts}_${rand}`;
};

/**
 * Helper to get cookie value by name
 * Used to retrieve Meta _fbc and _fbp cookies for advanced matching
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// KL-14130 (parallel to KL-14129 gclid fix): klikkie-website persists
// click IDs at landing to (a) sessionStorage.utm and (b) the cross-subdomain
// `_kk_utm` cookie so they survive the Adyen redirect that strips URL params.
// The server-side attribution snapshot is also echoed onto the order at
// checkout create (order.userAgent.attribution, KL-14380). All three are
// read here as fallbacks so Purchase's user_data still carries fbclid /
// ttclid / msclkid / epik / scid after the payment bounce.
const readSessionUtm = () => {
  try {
    const raw = window.sessionStorage?.getItem('utm');
    return raw ? JSON.parse(raw) || {} : {};
  } catch (_) {
    return {};
  }
};

const readAttributionCookie = () => {
  try {
    const raw = getCookie('_kk_utm');
    return raw ? JSON.parse(decodeURIComponent(raw)) || {} : {};
  } catch (_) {
    return {};
  }
};

/**
 * Hash email for Facebook CAPI advanced matching
 */
const hashEmailForFacebook = (email) => {
  if (!email) return null;
  return SHA256(email.toLowerCase().trim()).toString();
};

/**
 * Build comprehensive user_data object for enhanced conversions
 * Supports: Google Ads Enhanced Conversions, Meta CAPI, TikTok, etc.
 */
const buildUserData = (additionalData = {}, order = null) => {
  const userData = {};

  // Email - primary identifier for all platforms
  if (additionalData.email) {
    const normalizedEmail = additionalData.email.toLowerCase().trim();
    userData.email = normalizedEmail;
    userData.email_address = normalizedEmail;
    userData.sha256_email_address = hashEmailForFacebook(additionalData.email);
  }

  // Phone number (digits only)
  if (additionalData.phone) {
    userData.phone_number = additionalData.phone.replace(/\D/g, '');
  }

  // Name (for Google Enhanced Conversions)
  if (additionalData.firstName) userData.first_name = additionalData.firstName;
  if (additionalData.lastName) userData.last_name = additionalData.lastName;

  // Address data
  if (additionalData.city) userData.city = additionalData.city;
  if (additionalData.country) userData.country = additionalData.country;
  if (additionalData.postalCode) userData.postal_code = additionalData.postalCode;
  if (additionalData.street) userData.street = additionalData.street;

  // User/Customer ID (for cross-device matching)
  if (additionalData.userId) userData.user_id = additionalData.userId;
  if (additionalData.externalId) userData.external_id = additionalData.externalId;

  // Click IDs — fallback chain per param: URL → sessionStorage.utm →
  // _kk_utm cookie → order.userAgent.attribution → additionalData. Mirrors
  // klikkie-website's CLICK_IDS pattern (KL-14129 gclid fix, extended here
  // for fbclid/ttclid/msclkid/epik/scid — same class of Adyen-redirect leak).
  const urlParams = new URLSearchParams(window.location.search);
  const sessionUtm = readSessionUtm();
  const cookieUtm = readAttributionCookie();
  const orderAttr = (order && order.userAgent && order.userAgent.attribution) || {};

  const CLICK_ID_KEYS = ['fbclid', 'gclid', 'wbraid', 'gbraid', 'ttclid', 'msclkid', 'epik', 'scid'];
  for (const key of CLICK_ID_KEYS) {
    const value =
      urlParams.get(key) || sessionUtm[key] || cookieUtm[key] || orderAttr[key] || additionalData[key];
    if (value) userData[key] = value;
  }

  // Meta cookies (separate from click IDs — sourced from cookies, not URL).
  // fbc is also passed by klikkie-website via `_kk_utm` when the Pixel
  // never wrote a native `_fbc` (Instagram in-app webview, cross-context).
  const fbc =
    getCookie('_fbc') || sessionUtm.fbc || cookieUtm.fbc || orderAttr.fbc || additionalData.fbc;
  const fbp = getCookie('_fbp') || orderAttr.fbp || additionalData.fbp;
  if (fbc) userData.fbc = fbc;
  if (fbp) userData.fbp = fbp;

  // Client info
  userData.client_user_agent = navigator.userAgent;

  return userData;
};

// Funnel-cart SKUs that carry no collection/slug context locally — the SKU
// IS the variant. Mirrors site/data/sku-variant-map.json in klikkie-website
// (generated by site/scripts/build-sku-variant-map.js from the same source
// files build-product-feeds.js reads). Without this map the Pixel sends e.g.
// content_ids=['7'] on an AddToCart, but the Meta feed only has
// `subscriptions-7` rows -> 0% match rate (verified Jun 2026, parent
// observation 7a6d5b79).
//
// TODO(KL-14380-follow-up): extract this into a shared package both
// klikkie-website and klikkie-upload-webapp can import to avoid drift.
const SKU_TO_VARIANT = {
  // Subscriptions
  2: 'subscriptions-2',
  3: 'subscriptions-3',
  4: 'subscriptions-six-months',
  5: 'subscriptions-5',
  7: 'subscriptions-7',
  // Gift cards
  103: 'giftcards-1-year',
  104: 'giftcards-6-months',
  105: 'giftcards-3-months',
  120: 'giftcards-monthly',
  121: 'giftcards-photobook',
  122: 'giftcards-photobook-3-months',
  123: 'giftcards-photobook-6-months',
  124: 'giftcards-photobook-1-year',
  // Photo sets
  200: 'photosets-original-photo-set',
  250: 'photosets-no-biggie',
  251: 'photosets-medium-squares',
  252: 'photosets-squares-only',
  // Generic photobook SKUs (funnel "buy a Large/Pocket/XL" before design pick)
  603: 'photobooks-any-large',
  604: 'photobooks-any-pocket',
  605: 'photobooks-any-xl',
  // Wall decor (framed photo + framed canvas, M/L/XL)
  701: 'walldecors-framed-photo-m',
  702: 'walldecors-framed-photo-l',
  703: 'walldecors-framed-photo-xl',
  713: 'walldecors-framed-canvas-m',
  714: 'walldecors-framed-canvas-l',
  715: 'walldecors-framed-canvas-xl',
  // Photo cards
  751: 'photocards-photo-card-m',
  752: 'photocards-photo-card-l',
  753: 'photocards-photo-card-xl',
};

const variantId = (product = {}) => {
  if (product.variantId) return String(product.variantId);
  const { collection, slug, size, sku } = product;
  if (collection && slug) {
    return size ? `${collection}-${slug}-${size}` : `${collection}-${slug}`;
  }
  if (sku == null) return '';
  const mapped = SKU_TO_VARIANT[Number(sku)] || SKU_TO_VARIANT[String(sku)];
  if (mapped) return mapped;
  return String(sku);
};

// Transform order to GA4 ecommerce data structure
function transformOrderToGTM4(order) {
  // Populate the items.
  //
  // KL-14380 follow-up: drop lines whose item_id stays bare-numeric (no
  // hyphen → not in any composite-id Meta-feed row). Accessories (extra
  // pages 613/614/615), the no-plan-picked sentinel sku=0, and assorted
  // bundle/legacy SKUs used to surface as "Item deleted from catalog" in
  // Meta diagnostics. `value` is taken from `order.totalToCharge`
  // independently so revenue attribution is unaffected.
  const items = (order.products || [])
    .map((product) => ({
      item_id: variantId(product),
      item_name: product.name,
      item_brand: 'klikkie',
      item_category: product.type,
      price: product.unitPrice.toString(),
    }))
    .filter((item) => typeof item.item_id === 'string' && item.item_id.includes('-'));

  // Return ecommerce data directly (not wrapped)
  return {
    transaction_id: order.id,
    currency: order.currency,
    value: order.totalToCharge === 0 ? 0 : order.totalToCharge / 100,
    items,
    coupon: order.discountCode,
    discount: order.totalDiscount === 0 ? 0 : order.totalDiscount / 100,
  };
}

export const trackingClick = (eventCategory, eventLabel, order, userData = {}) => {
  if (!eventCategory || !eventLabel) {
    console.error('Error: eventCategory and eventLabel are required for trackingClick.');
    return;
  }

  const event_id = generateEventId('click');

  const event = {
    dataLayer: {
      event: 'event',
      event_id,
      eventCategory,
      eventAction: 'click',
      eventLabel,
    },
  };
  if (order) {
    event.dataLayer.ecommerce = transformOrderToGTM4(order);
  }

  // Add user_data for enhanced conversions if provided or if it's a purchase event
  if (eventLabel === 'purchase' || Object.keys(userData).length > 0) {
    event.dataLayer.user_data = buildUserData(userData, order);
  }

  TagManager.dataLayer(event);
};

/**
 * Track purchase event with comprehensive user data for enhanced conversions
 * This is the main function to use for purchase events
 */
export const trackPurchase = (order, userData = {}) => {
  if (!order) {
    console.error('Error: order is required for trackPurchase.');
    return;
  }

  // Use order.id as event_id for purchase to enable sGTM deduplication
  // This must match the event_id sent by the backend to Meta CAPI
  const event_id = `${order.id}`;

  const event = {
    dataLayer: {
      event: 'purchase',
      event_id,
      eventCategory: 'checkout',
      eventAction: 'click',
      eventLabel: 'purchase',
      ecommerce: transformOrderToGTM4(order),
      user_data: buildUserData(userData, order),
    },
  };

  TagManager.dataLayer(event);
  console.log('Purchase event tracked with user_data:', event.dataLayer);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fire the purchase event only once the payment has actually settled.
 *
 * The recurrent-card charge settles asynchronously (Adyen webhook), so firing
 * `purchase` right after the order call reported conversions for charges that
 * were later rejected — most webapp orders reject (stored cards expire), so
 * the majority of webapp Purchase events were for unpaid orders.
 *
 * Polls the payment status a few times; fires on `fulfilled`, stops on
 * `rejected`. If the webhook is slower than the last poll we give up silently:
 * the backend CAPI event (klikkie-orders → sGTM, same event_id = order.id)
 * still reports the conversion when the order fulfils, so no purchase is lost
 * — only the browser-side duplicate.
 */
export const trackPurchaseWhenSettled = async (order, userData = {}, options = {}) => {
  const { getPaymentStatus, delaysMs = [0, 5000, 15000] } = options;
  if (!order?.id) {
    console.error('Error: order is required for trackPurchaseWhenSettled.');
    return false;
  }
  if (typeof getPaymentStatus !== 'function') {
    console.error('Error: getPaymentStatus is required for trackPurchaseWhenSettled.');
    return false;
  }

  for (let i = 0; i < delaysMs.length; i += 1) {
    if (delaysMs[i] > 0) await wait(delaysMs[i]);
    let status;
    try {
      status = await getPaymentStatus(order.id);
    } catch (error) {
      console.warn('Payment status lookup failed, retrying:', error);
    }
    if (status === 'fulfilled') {
      trackPurchase(order, userData);
      return true;
    }
    if (status === 'rejected') {
      console.log('Payment rejected, purchase event suppressed for order:', order.id);
      return false;
    }
  }
  console.log('Payment not settled in time, leaving purchase to backend CAPI for order:', order.id);
  return false;
};